import { call, put, take, takeEvery } from 'redux-saga/effects';
import { getLogger } from 'utils/logger';
import store from 'store';

import { addPostponedAction } from 'data/postponedActions/postponedActionsActions';
import { shouldPostponeAction } from 'data/postponedActions/postponedActionsHelpers';

import {
  getQcsSyncResources,
  qcsSyncCreateResource,
  qcsSyncUpdateResource,
  qcsSyncDeleteResource,
} from 'data/qcsSyncResourceSagaHelpers';

import { mkQcsSyncResourceConfig } from 'data/qcsSyncResourceTypes';
import { transformResponseToFilters, transformFilterToRequestData } from './transformers';

import * as actions from './actions';
import { getLastSyncDate } from './selectors';

import AxiosFactory from '../../utils/axiosFactory';
import { Config, ENVIRONMENT } from '../../utils/webappConfig';
import * as spendingWatchListAction from '../spendingWatchList/actions';
const qcsAxios = AxiosFactory.get('qcs');

const log = getLogger('data/filters/sagas');

const mkResourceConfig = (successAction, failureAction) => mkQcsSyncResourceConfig({
  resourceName: 'filter',
  resourceBaseUrl: '/filters',
  getLastSyncDate,

  transformResponseToResources: transformResponseToFilters,
  transformResourceToRequestData: transformFilterToRequestData,

  successAction,
  failureAction,
});

export function* getFiltersActionWatcher() {
  // using a vanilla saga pattern so we only process a single get request
  //  - takeEvery would process every request
  //  - takeLatest would cancel any current request and start request again
  while (true) {
    const action = yield take(actions.getFilters);

    const config = mkResourceConfig(actions.getFiltersSuccess, actions.getFiltersFailure);
    yield call(getQcsSyncResources, config, action);
  }
}

export function* createSpendingWatchListFilterAction(action: any) {
  const config = mkResourceConfig(actions.createFilterSuccess, actions.createFilterFailure);
  try {
    const { filterItems: resource } = action;

    const requestUrl = `${Config.environments[ENVIRONMENT].services_url}${config.resourceBaseUrl}`;
    const requestData = config.transformResourceToRequestData(resource);

    const response = yield call(qcsAxios.post, requestUrl, requestData, config.axiosConfig);

    const resultingResource = config.transformResourceWithResponse(resource, response);
    yield put(config.successAction(resultingResource, action.meta));

    if (action.meta && action.meta.afterSuccessAction) {
      yield put(action.meta.afterSuccessAction);
    }
    return resultingResource;
  } catch (error) {
    log.error(`Error creating ${config.resourceName}`, error);
    if (shouldPostponeAction(action, error)) {
      yield put(addPostponedAction({ action, error, onFailure: () => store.dispatch(config.failureAction(error, action.meta)) }));
    } else {
      yield put(config.failureAction(error, action.meta));
    }
  }
  return null;
}

export function* createFilterActionWatcher() {
  const config = mkResourceConfig(actions.createFilterSuccess, actions.createFilterFailure);
  yield takeEvery(actions.createFilter, qcsSyncCreateResource, config);
}

export function* updateFilterActionWatcher() {
  const config = mkResourceConfig(actions.updateFilterSuccess, actions.updateFilterFailure);
  yield takeEvery(actions.updateFilter, qcsSyncUpdateResource, config);
}

export function* updateFilterSuccessActionWatcher() {
  while (true) {
    const action = yield take(actions.updateFilterSuccess);
    if (action) {
      yield put(spendingWatchListAction.getSpendingWatchLists());
    }
  }
}

export function* deleteFilterActionWatcher() {
  const config = mkResourceConfig(actions.deleteFilterSuccess, actions.deleteFilterFailure);
  yield takeEvery(actions.deleteFilter, qcsSyncDeleteResource, config);
}

export default [
  getFiltersActionWatcher,
  createFilterActionWatcher,
  updateFilterActionWatcher,
  deleteFilterActionWatcher,
  updateFilterSuccessActionWatcher,
];


